<template>
  <div class="page">
    <div style="display:flex;align-items:center;justify-content:space-between;gap:10px;">
      <h2 class="page-title" style="margin:0;">{{ $t('menu.secondaryClosed') }}</h2>
      <el-button @click="load" :loading="loading">{{ $t('common.refresh') }}</el-button>
    </div>

    <div class="glass-card" style="margin-top:14px; padding:14px; overflow-x:auto;">
      <el-table :data="itemsPage" style="width:100%;" @row-click="open">
        <el-table-column :label="$t('field.caseNo')" width="150">
          <template slot-scope="s">
            <span :class="['case-no-cell', s.row.hasNewNotice ? 'case-no-cell--new' : '']">
              <span v-if="s.row.hasNewNotice" class="row-dot" />
              {{ s.row.caseNo }}
            </span>
          </template>
        </el-table-column>
        <el-table-column prop="title" :label="$t('field.title')" min-width="220" />
        <el-table-column prop="status" :label="$t('field.status')" width="170">
          <template slot-scope="s">
            <span :class="['tag-pill', s.row.hasNewNotice ? 'tag-pill--with-dot' : '']">
              {{ caseStatusLabel(s.row) }}
              <span v-if="s.row.hasNewNotice" class="status-pill-dot" />
            </span>
          </template>
        </el-table-column>
        <el-table-column :label="flowColumnLabel" width="220">
          <template slot-scope="s">
            <span class="tag-pill">{{ flowLabel(s.row) }}</span>
          </template>
        </el-table-column>
        <el-table-column :label="$t('ticket.disputeLabel')" width="170">
          <template slot-scope="s">
            <span v-if="s.row.disputeStatus" :class="['tag-pill', s.row.disputeStatus === 'DISPUTE_CLOSED' ? '' : 'tag-pill--warn']">
              {{ disputeLabel(s.row) }}
            </span>
            <span v-else class="muted">-</span>
          </template>
        </el-table-column>
        <el-table-column prop="createdAtLabel" :label="$t('field.createdAt')" width="190" />
        <el-table-column prop="closedAtLabel" :label="$t('field.closedAt')" width="190" />
      </el-table>
      <div style="display:flex;justify-content:flex-end;margin-top:12px;">
        <el-pagination
          background
          layout="total, sizes, prev, pager, next"
          :total="items.length"
          :page-size="pageSize"
          :current-page="page"
          :page-sizes="[5,10,20,50]"
          @current-change="(p) => (page = p)"
          @size-change="onSizeChange"
        />
      </div>
    </div>

    <SecondaryCaseDialog
      v-model="detailDialog"
      :case-no="currentCaseNo"
      mode="closed"
      @changed="load"
    />
  </div>
</template>

<script>
import { secondaryClosedCases, secondaryMarkCaseSeen } from '../../api/secondary';
import SecondaryCaseDialog from '../../components/SecondaryCaseDialog.vue';
import { caseStatusLabel as formatCaseStatusLabel, disputeStatusLabel as formatDisputeStatusLabel, flowLabel as formatFlowLabel, localText as formatLocalText } from '../../utils/caseDisplay';

export default {
  name: 'SecondaryClosed',
  components: { SecondaryCaseDialog },
  data() {
    return { loading: false, items: [], page: 1, pageSize: 10, detailDialog: false, currentCaseNo: '' };
  },
  created() {
    this.load();
    this.maybeOpenFromQuery();
  },
  computed: {
    version() {
      return this.$store.state.versions.secondaryCases;
    },
    itemsPage() {
      const start = (this.page - 1) * this.pageSize;
      return (this.items || []).slice(start, start + this.pageSize);
    },
    flowColumnLabel() {
      return formatLocalText(this, '流转', 'Flow');
    }
  },
  watch: {
    version() {
      this.load();
    },
    detailDialog(v) {
      if (v) return;
      if (this.$route.query && this.$route.query.caseNo) {
        const q = { ...this.$route.query };
        delete q.caseNo;
        this.$router.replace({ path: this.$route.path, query: q }).catch(() => {});
      }
    },
    '$route.query.caseNo'() {
      this.maybeOpenFromQuery();
    }
  },
  methods: {
    maybeOpenFromQuery() {
      const caseNo = this.$route.query && this.$route.query.caseNo ? String(this.$route.query.caseNo) : '';
      if (!caseNo) return;
      this.currentCaseNo = caseNo;
      this.detailDialog = true;
    },
    async load() {
      this.loading = true;
      try {
        const r = await secondaryClosedCases();
        const base = (r || []).map((x) => ({
          ...x,
          createdAtLabel: x.createdAt ? new Date(x.createdAt).toLocaleString() : '-',
          closedAtLabel: x.closedAt ? new Date(x.closedAt).toLocaleString() : '-'
        }));
        this.items = base.sort((a, b) => {
          if (!!a.hasNewNotice !== !!b.hasNewNotice) return a.hasNewNotice ? -1 : 1;
          return new Date(b.closedAt || b.createdAt || 0).getTime() - new Date(a.closedAt || a.createdAt || 0).getTime();
        });
        const maxPage = Math.max(1, Math.ceil(this.items.length / this.pageSize));
        this.page = Math.min(this.page, maxPage);
      } finally {
        this.loading = false;
      }
    },
    onSizeChange(s) {
      this.pageSize = s;
      this.page = 1;
    },
    async open(row) {
      if (!row || !row.caseNo) return;
      try {
        await secondaryMarkCaseSeen(row.caseNo);
        this.items = (this.items || []).map((x) => (x && x.caseNo === row.caseNo ? { ...x, hasNewNotice: false } : x));
        await this.$store.dispatch('refreshSecondaryBadges');
      } catch (e) {}
      this.currentCaseNo = row.caseNo;
      this.detailDialog = true;
    },
    disputeLabel(row) {
      return formatDisputeStatusLabel(this, row);
    },
    caseStatusLabel(row) {
      return formatCaseStatusLabel(this, row);
    },
    flowLabel(row) {
      return formatFlowLabel(this, row);
    }
  }
};
</script>

<style scoped>
.case-no-cell {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}
.case-no-cell--new {
  font-weight: 600;
  color: #1d4ed8;
}
.row-dot,
.status-pill-dot {
  display: inline-block;
  width: 7px;
  height: 7px;
  border-radius: 999px;
  background: #f56c6c;
  box-shadow: 0 0 0 3px rgba(245, 108, 108, 0.12);
}
.tag-pill--with-dot {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}
</style>
